/**
 * ``useTrackSseBridge`` — feeds the live ``status``-kind SSE position
 * deltas into the dynamic-track position store (issue #278 follow-up,
 * PR1).
 *
 * The Map already receives status deltas (the snapshot layer merges them
 * for marker positions); this bridge additionally appends each one to the
 * per-sensor track buffer so the :class:`TrackTailLayer` can draw the
 * recent path behind each marker. Shaped as a prop-driven hook (taking
 * the Map's ``connection.subscribe``) to mirror
 * :func:`detections/use-detections-sse-bridge` and unit-test without a
 * provider.
 *
 * Every position reaches this bridge, including several in one flush (issue
 * #739). The single-slot ``lastMessage`` this used to read dropped the
 * earlier positions of a burst, visibly coarsening the drawn tail whenever
 * updates arrived faster than React rendered — worst under exactly the
 * high-rate conditions where the tail is most informative. PR2's server
 * backfill + the store's monotonic guard remain the defence against genuine
 * stream gaps; they are no longer covering for client-side loss.
 *
 * Cross-references:
 *   - map/sse-subscription.ts (the fan-out) · map/track-store.ts.
 *   - REQ-UI-006 (live update latency).
 */
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import { useTrackStore } from "@/map/track-store";

/**
 * Append every arriving ``status``-kind SSE position to the track store.
 * No-op for every other message kind.
 *
 * @param subscribe - The Map's ``connection.subscribe``. ``null`` mounts no
 *     subscription (no connection yet).
 */
export function useTrackSseBridge(subscribe: SseSubscribe | null): void {
  useSseMessages(subscribe, (message) => {
    if (message.kind === "status") {
      const payload = message.payload;
      useTrackStore.getState().append(payload.producer_id, {
        latitude_deg: payload.latitude_deg,
        longitude_deg: payload.longitude_deg,
        time: payload.time,
        health_state: payload.health_state,
      });
    }
  });
}
